import { Text } from "@ecco/ui/text";
import { Divider } from "@ecco/ui/divider";
import { Button } from "@ecco/ui/button";
import { Box } from "@ecco/ui/box";
import { Flex } from "@ecco/ui/flex";
import { Progress } from "@ecco/ui/progress";
import BaseInputField from "../BaseInputField";
import { motionBoxProps, styles } from "./addressLookupStyles";
import type { UseAddressLookupParams } from "./useAddressLookup";
import { useAddressLookup } from "./useAddressLookup";
import {
    ADDRESS_LOOKUP_OPTION_TEST_ID,
    ADDRESS_LOOKUP_TEST_ID,
} from "@e2e/helpers/checkout/components/shipping-details/constants";
import type { SingleFieldProps } from "../baseFormsValidations";

type SingleFieldPropsWihoutHandlers = Omit<SingleFieldProps, "onChange">;

type AddressLookupProps = SingleFieldPropsWihoutHandlers &
    UseAddressLookupParams & {
        tooltipLabel: string;
        noAddressesFound: string;
        showTooltip: boolean;
    };

export const AddressLookup = ({
    id,
    isSuccess,
    prefix,
    country,
    locale,
    tooltipLabel,
    noAddressesFound,
    rightIcon,
    showTooltip,
    onAddressSelect,
    testId = ADDRESS_LOOKUP_TEST_ID,
    ...baseInputFieldProps
}: AddressLookupProps) => {
    const {
        dropdownRef,
        onClickAddress,
        onChange: onInputChange,
        addresses,
        isLoading,
        hasError,
        isOpen,
        getFullAddress,
    } = useAddressLookup({
        id,
        prefix,
        country,
        locale,
        onAddressSelect,
    });

    const showAsSuccess = isSuccess && !hasError;
    const computedRightIcon = showAsSuccess ? rightIcon : undefined;

    return (
        <Flex key={id} sx={styles.fieldWrapper}>
            <BaseInputField
                {...baseInputFieldProps}
                isSuccess={showAsSuccess}
                rightIcon={computedRightIcon}
                testId={testId}
                id={id}
                onChange={onInputChange}
            />

            <Box
                ref={dropdownRef}
                {...motionBoxProps}
                height={!isOpen ? 0 : isLoading || hasError ? "65px" : "329px"}
                visibility={isOpen ? "visible" : "hidden"}
                transitionDelay={isOpen ? "0s, 0s" : "0s, 0.5s"}
                overflow="hidden"
            >
                {isLoading || hasError ? (
                    <Placeholder errorText={noAddressesFound} isLoading={isLoading} />
                ) : (
                    <Flex sx={styles.addressesWrapper}>
                        {addresses?.map((address) => {
                            const addressDisplay =
                                address.extra?.fullAddress ?? getFullAddress(address);
                            return (
                                <Flex
                                    key={addressDisplay}
                                    direction="column"
                                    alignItems="flex-start"
                                >
                                    <Button
                                        data-testid={ADDRESS_LOOKUP_OPTION_TEST_ID}
                                        onClick={() => onClickAddress(address)}
                                        variant="ghost"
                                        sx={styles.addressButton}
                                        type="button"
                                    >
                                        {addressDisplay}
                                    </Button>
                                    <Divider sx={styles.divider} />
                                </Flex>
                            );
                        })}
                    </Flex>
                )}
            </Box>

            {showTooltip && <Text sx={styles.tooltipText}>{tooltipLabel}</Text>}
        </Flex>
    );
};

const Placeholder = ({ isLoading, errorText }: { isLoading: boolean; errorText?: string }) => {
    if (isLoading) {
        return <Progress sx={styles.progress} variant="black" isIndeterminate />;
    }
    if (errorText) {
        return <Text sx={styles.noAddressesText}>{errorText}</Text>;
    }
};
